import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Music, Globe, Loader2, Pencil, ArrowRight, ArrowLeft, X, ImageIcon, LogOut, ListMusic, Check, ShieldCheck, Heart, Headphones } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API, fileUrl } from '../api';
import { AUDIO } from '../mock';
import AudioPlayer from '../components/AudioPlayer';
import FileDrop from '../components/FileDrop';

const TONES = ['Warm', 'Trustworthy', 'Family-first', 'Confident', 'Joyful', 'Honest', 'Bright', 'Faith-filled', 'Playful', 'Upscale', 'Neighborly', 'Bold'];
const STYLES = ['R&B', 'Soul', 'Gospel', 'Pop', 'Hip-Hop', 'Acoustic', 'Country', 'Jazz', 'Surprise me'];
const LENGTHS = ['Jingle (15\u201330 seconds)', 'Short song (about 60 seconds)', 'Full song (2\u20133 minutes)'];
const STEPS = ['Your business', 'Your song', 'Send it'];
const energyLabel = (n) => (n < 25 ? 'Calm' : n < 50 ? 'Warm' : n < 75 ? 'Upbeat' : 'High energy');

export default function StartSong() {
  const nav = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    if (user?.role === 'studio') { toast.info('Use "Add customer" in the studio to start a song for a client.'); nav('/studio', { replace: true }); }
  }, [user]); // eslint-disable-line
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState('ai'); // ai | manual
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serviceInput, setServiceInput] = useState('');
  const [files, setFiles] = useState([]); // {id,url,name,kind}
  const [sample, setSample] = useState(null);
  useEffect(() => { API.sampleSong().then(setSample).catch(() => {}); }, []);

  const [p, setP] = useState({ business: '', type: '', city_state: '', website: '', does: '', serves: '', different: '', phone: '', services: [], tones: [] });
  const [brief, setBrief] = useState(null);
  const [usedAI, setUsedAI] = useState(false);
  const [prefs, setPrefs] = useState({ genres: [], energy: 50, length: LENGTHS[1], words: '', loves: '', notes: '' });
  const [yourName, setYourName] = useState(user?.owner_name || '');
  useEffect(() => { if (user?.owner_name && !yourName) setYourName(user.owner_name); }, [user]); // eslint-disable-line

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const read = async () => {
    setReading(true);
    try {
      const data = await API.readSite(p.website);
      const pr = data.profile || {};
      setP((prev) => ({
        ...prev,
        business: pr.business || prev.business, type: pr.type || prev.type,
        city_state: pr.city_state || prev.city_state, website: pr.website || prev.website,
        does: pr.does || prev.does, serves: pr.serves || prev.serves, different: pr.different || prev.different,
        phone: pr.phone || prev.phone, services: pr.services && pr.services.length ? pr.services : prev.services,
        tones: pr.tones && pr.tones.length ? pr.tones : prev.tones,
      }));
      setBrief(data.brief || null);
      setUsedAI(true);
      toast.success('Your website read. Check everything and tweak as you like.');
    } catch (e) { toast.error('Could not read that site \u2014 you can fill it in by hand.'); }
    finally { setReading(false); }
  };

  const addService = (e) => {
    if (e.key === 'Enter' && serviceInput.trim()) {
      e.preventDefault();
      set('services', [...p.services, serviceInput.trim()]);
      setServiceInput('');
    }
  };
  const removeService = (s) => set('services', p.services.filter((x) => x !== s));
  const toggleTone = (t) => set('tones', p.tones.includes(t) ? p.tones.filter((x) => x !== t) : [...p.tones, t]);
  const toggleGenre = (g) => setPrefs((pr) => ({ ...pr, genres: pr.genres.includes(g) ? pr.genres.filter((x) => x !== g) : [...pr.genres, g] }));
  const onUploaded = (res, f) => setFiles((prev) => [...prev, { id: res.id, url: res.url, name: f.name, kind: prev.length === 0 ? 'logo' : 'photo' }]);
  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id).map((f, i) => ({ ...f, kind: i === 0 ? 'logo' : 'photo' })));

  const submit = async () => {
    const payload = { profile: { ...p, owner: yourName || user?.owner_name, ai: usedAI, brief, profile: p.does, files }, prefs: { ...prefs, energy: energyLabel(prefs.energy) } };
    if (!user) {
      localStorage.setItem('bm_pending', JSON.stringify(payload));
      toast.message('One quick step', { description: 'Create your account to send this to Perry.' });
      nav('/signup');
      return;
    }
    setBusy(true);
    try {
      await API.createRequest(payload);
      toast.success('Sent to Perry.', { description: 'You\u2019ll get an email when your versions are ready.' });
      nav('/dashboard');
    } catch (e) { toast.error('Could not send your request.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-ink grain">
      <div className="border-b hairline">
        <div className="max-w-6xl mx-auto px-6 h-[76px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="grid place-items-center w-11 h-11 rounded-full border hairline"><Music size={18} className="text-gold" /></span>
            <span className="font-display text-xl text-cream">Brand <span className="italic text-gold-grad">Melody</span><span className="block eyebrow text-[8px] mt-0.5">BY P. FRAZY</span></span>
          </Link>
          <div className="flex items-center gap-3">
            {user && <button onClick={() => nav('/dashboard')} className="btn-ghost rounded-full px-4 py-2 text-sm flex items-center gap-2"><ListMusic size={15} className="text-gold" /> My songs</button>}
            {user && <button onClick={() => nav('/')} className="btn-ghost rounded-full px-4 py-2 text-sm flex items-center gap-2"><LogOut size={15} /> Sign out</button>}
            {!user && <button onClick={() => nav('/signin')} className="text-sm text-cream/75 hover:text-cream px-2">Sign in</button>}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="eyebrow mb-3">New song request</p>
        <h1 className="font-display text-5xl md:text-6xl text-cream leading-[0.95]">Let&rsquo;s write <span className="italic text-gold-grad">your song.</span></h1>
        <p className="mt-3 text-cream/60">Takes about three minutes. Less if you paste your website.</p>

        <div className="mt-10 grid lg:grid-cols-[260px_1fr] gap-8">
          {/* vertical stepper */}
          <div className="space-y-2 self-start">
            {STEPS.map((s, i) => (
              <button key={s} data-testid={`step-${i}`} onClick={() => i < step && setStep(i)} className={`w-full flex items-center gap-3 px-5 py-4 rounded-2xl text-left transition-colors ${i === step ? 'bg-white/[0.035]' : ''} ${i < step ? 'cursor-pointer hover:bg-white/[0.02]' : 'cursor-default'}`}>
                <span className={`grid place-items-center w-7 h-7 rounded-full text-xs font-semibold ${i < step ? 'border border-gold/60 text-gold' : i === step ? 'bg-gold text-ink' : 'border border-cream/20 text-cream/45'}`}>{i < step ? <Check size={13} /> : i + 1}</span>
                <span className={i === step ? 'text-cream' : i < step ? 'text-cream/70' : 'text-cream/45'}>{s}</span>
              </button>
            ))}
          </div>

          {/* panel */}
          <div className="panel rounded-3xl p-7">
            {step === 0 && (
              <div className="space-y-6">
                <div className="inline-flex items-center p-1 rounded-full border hairline">
                  <button onClick={() => setMode('ai')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${mode === 'ai' ? 'bg-gold/15 text-cream' : 'text-cream/55 hover:text-cream'}`}><Globe size={15} className="text-gold" /> Read my website <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-gold/20 text-gold">AI</span></button>
                  <button onClick={() => setMode('manual')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${mode === 'manual' ? 'bg-gold/15 text-cream' : 'text-cream/55 hover:text-cream'}`}><Pencil size={14} /> I&rsquo;ll fill it in</button>
                </div>

                {mode === 'ai' && (
                  <div className="flex items-center gap-3 rounded-full border hairline px-4 py-2 focus-within:border-gold/50 transition-colors">
                    <Globe size={18} className="text-gold shrink-0" />
                    <input value={p.website} onChange={(e) => set('website', e.target.value)} placeholder="yourbusiness.com" className="bg-transparent flex-1 outline-none text-cream placeholder:text-cream/35 py-1" />
                    <button onClick={read} disabled={reading} className="btn-gold rounded-full px-4 py-1.5 text-sm flex items-center gap-2 disabled:opacity-70">{reading ? <><Loader2 size={15} className="animate-spin" /> Reading</> : 'Read my site'}</button>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Business name" value={p.business} onChange={(v) => set('business', v)} />
                  <Field label="Industry" value={p.type} onChange={(v) => set('type', v)} />
                  <Field label="City and state" value={p.city_state} onChange={(v) => set('city_state', v)} />
                  <Field label="Website" value={p.website} onChange={(v) => set('website', v)} />
                </div>

                <Area label="What you do" value={p.does} onChange={(v) => set('does', v)} rows={3} />

                <div>
                  <label className="eyebrow text-[9px] block mb-2">Services</label>
                  <div className="rounded-xl border hairline bg-panel2/50 p-3 focus-within:border-gold/50 transition-colors">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {p.services.map((s) => (
                        <span key={s} className="flex items-center gap-1.5 text-sm text-cream rounded-full border border-gold/40 bg-gold/[0.06] pl-3 pr-2 py-1">{s}<button onClick={() => removeService(s)} className="text-cream/50 hover:text-heart transition-colors"><X size={13} /></button></span>
                      ))}
                    </div>
                    <input value={serviceInput} onChange={(e) => setServiceInput(e.target.value)} onKeyDown={addService} placeholder="Add a service, press Enter" className="w-full bg-transparent outline-none text-cream placeholder:text-cream/35 text-sm" />
                  </div>
                </div>

                <Area label="What makes you different" value={p.different} onChange={(v) => set('different', v)} rows={3} />
                <Field label="Who your customers are" value={p.serves} onChange={(v) => set('serves', v)} />
                <Field label="Phone (if you want it in the song)" value={p.phone} onChange={(v) => set('phone', v)} />

                <div>
                  <label className="eyebrow text-[9px] block mb-3">How you sound</label>
                  <div className="flex flex-wrap gap-2">
                    {Array.from(new Set([...TONES, ...p.tones])).map((t) => (
                      <button key={t} onClick={() => toggleTone(t)} className={`px-4 py-2 rounded-full border text-sm transition-colors ${p.tones.includes(t) ? 'border-gold/60 text-gold bg-gold/[0.06]' : 'hairline text-cream/70 hover:border-gold/40'}`}>{t}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="eyebrow text-[9px] block mb-2">Logo and photos (optional)</label>
                  <FileDrop accept="image/*" multiple onUploaded={onUploaded} testId="logo-photo-upload">
                    <span className="flex items-center gap-2"><ImageIcon size={16} className="text-gold/70" /> {files.length ? `${files.length} file${files.length > 1 ? 's' : ''} attached. Add more?` : 'Add your logo first, then a few photos.'}</span>
                  </FileDrop>
                  {files.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {files.map((f) => (
                        <div key={f.id} className="relative group" data-testid={`uploaded-file-${f.id}`}>
                          <img src={fileUrl(f.url)} alt={f.name} className="w-16 h-16 rounded-lg object-cover ring-1 ring-gold/20" />
                          {f.kind === 'logo' && <span className="absolute bottom-1 left-1 text-[8px] font-mono tracking-widest bg-gold text-ink rounded px-1">LOGO</span>}
                          <button onClick={() => removeFile(f.id)} className="absolute -top-1.5 -right-1.5 grid place-items-center w-5 h-5 rounded-full bg-ink border border-gold/40 text-cream/70 hover:text-heart transition-colors"><X size={11} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t hairline">
                  <span className="flex items-center gap-2 text-xs text-cream/45"><ShieldCheck size={14} className="text-gold" /> Only Perry sees your profile.</span>
                  <button onClick={() => setStep(1)} className="btn-gold rounded-full px-7 py-3.5 flex items-center gap-2">Continue to your song <ArrowRight size={17} /></button>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-7" data-testid="step-your-song">
                <div>
                  <h2 className="font-display text-3xl md:text-4xl text-cream">How should it <span className="italic text-gold-grad">sound?</span></h2>
                  <p className="mt-2 text-cream/60">Point Perry in a direction. He&rsquo;ll take it from there.</p>
                </div>
                <div>
                  <label className="text-xs text-cream/60 block mb-3">Style (pick any)</label>
                  <div className="flex flex-wrap gap-2">
                    {STYLES.map((g) => <button key={g} data-testid={`style-${g}`} onClick={() => toggleGenre(g)} className={`px-4 py-2 rounded-full border text-sm transition-colors ${prefs.genres.includes(g) ? 'border-gold/60 text-gold bg-gold/[0.06]' : 'hairline text-cream/85 bg-white/[0.03] hover:border-gold/40'}`}>{g}</button>)}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-cream/60 block mb-3">Energy</label>
                  <input data-testid="energy-slider" type="range" min="0" max="100" value={prefs.energy} onChange={(e) => setPrefs({ ...prefs, energy: Number(e.target.value) })} className="range-gold w-full" style={{ '--pct': `${prefs.energy}%` }} />
                  <div className="flex justify-between mt-2 text-xs text-cream/50"><span>Calm and warm</span><span>High energy</span></div>
                </div>
                <div>
                  <label className="text-xs text-cream/60 block mb-3">Length</label>
                  <div className="flex flex-wrap gap-2">
                    {LENGTHS.map((o) => <button key={o} data-testid={`length-${o}`} onClick={() => setPrefs({ ...prefs, length: o })} className={`px-4 py-2.5 rounded-full border text-sm transition-colors ${prefs.length === o ? 'border-gold/60 text-gold bg-gold/[0.06]' : 'hairline text-cream/85 bg-white/[0.03] hover:border-gold/40'}`}>{o}</button>)}
                  </div>
                </div>
                <Field label="Words it must include" value={prefs.words} onChange={(v) => setPrefs({ ...prefs, words: v })} placeholder="Your business name, a slogan, your phone number..." />
                <Field label="Songs you love (optional)" value={prefs.loves} onChange={(v) => setPrefs({ ...prefs, loves: v })} placeholder="Artists or songs that feel like your brand" />
                <Area label="Anything else Perry should know?" value={prefs.notes} onChange={(v) => setPrefs({ ...prefs, notes: v })} rows={3} placeholder="Where will it play? Radio, your website, the waiting room?" />
                <div className="flex items-center justify-between pt-6 border-t hairline">
                  <button data-testid="song-back-btn" onClick={() => setStep(0)} className="btn-ghost rounded-full px-6 py-3.5 flex items-center gap-2 text-sm"><ArrowLeft size={16} /> Back</button>
                  <button data-testid="song-continue-btn" onClick={() => setStep(2)} className="btn-gold rounded-full px-7 py-3.5 flex items-center gap-2">Continue <ArrowRight size={17} /></button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6" data-testid="step-send-it">
                <div>
                  <h2 className="font-display text-3xl md:text-4xl text-cream">Ready to <span className="italic text-gold-grad">send?</span></h2>
                  <p className="mt-2 text-cream/60">{user ? <>Signed in as {user.email}.</> : 'You\u2019ll create your account on the next step.'}</p>
                </div>
                <div className="sm:w-1/2 sm:pr-2">
                  <Field label="Your name" value={yourName} onChange={setYourName} placeholder="Who should Perry write to?" />
                </div>
                <div className="rounded-2xl border hairline bg-panel2/40 p-5 flex flex-col sm:flex-row sm:items-start justify-between gap-4" data-testid="request-summary">
                  <div>
                    <p className="text-xs text-cream/55">You&rsquo;re requesting</p>
                    <p className="font-display text-2xl text-cream mt-1">{p.business || 'Your business'}</p>
                    <p className="text-sm text-cream/60 mt-1">{prefs.genres.length ? prefs.genres.join(', ') : 'Any style'} &middot; {prefs.length}</p>
                  </div>
                  <p className="flex items-start gap-2 text-xs text-cream/50 sm:max-w-[300px]"><ShieldCheck size={14} className="text-gold shrink-0 mt-0.5" /> No payment now. You&rsquo;ll see your price with your song.</p>
                </div>
                <div className="rounded-2xl border border-gold/20 bg-gold/[0.03] p-5" data-testid="sample-song-card">
                  <p className="flex items-center gap-2 text-xs text-cream/60 mb-3"><Headphones size={14} className="text-gold" /> Hear Perry&rsquo;s sound while you decide {sample?.title && <span className="text-cream/40">&middot; {sample.title}</span>}</p>
                  <AudioPlayer id="sample-song-start" src={sample?.url || AUDIO[0]} compact />
                </div>
                <div className="flex items-center justify-between pt-6 border-t hairline">
                  <button data-testid="send-back-btn" onClick={() => setStep(1)} className="btn-ghost rounded-full px-6 py-3.5 flex items-center gap-2 text-sm"><ArrowLeft size={16} /> Back</button>
                  <button data-testid="send-to-perry-btn" onClick={submit} disabled={busy} className="btn-gold rounded-full px-7 py-3.5 flex items-center gap-2 disabled:opacity-70">{busy ? <Loader2 size={16} className="animate-spin" /> : <>Send to Perry <Heart size={17} /></>}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-xs text-cream/60 block mb-2">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream placeholder:text-cream/35 outline-none focus:border-gold/50 transition-colors" />
    </div>
  );
}
function Area({ label, value, onChange, rows = 3, placeholder }) {
  return (
    <div>
      <label className="text-xs text-cream/60 block mb-2">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder} className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream placeholder:text-cream/35 outline-none focus:border-gold/50 transition-colors resize-none" />
    </div>
  );
}
